import { useState, useEffect } from "react";
import styles from "./Kanban.module.scss";
import { useDraggable } from "@dnd-kit/core";
import { dateOnlyFormatted } from "../../utils/systemSettings";
import {
  CalendarIconDate,
  MeatBallsMenuIcon,
  DeleteIcon,
  EditIcon,
  ChevronRightIcon,
  ToDoBadge,
  InProgressBadge,
  DoneBadge,
  SlippingBadge,
  OnHoldBadge,
  AttachementIcon,
} from "../../modules/default-svg";
import {
  Avatar,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Tooltip,
} from "@fluentui/react-components";

import {
  AvatarGroup,
  AvatarGroupItem,
  AvatarGroupPopover,
  partitionAvatarGroupItems,
} from "@fluentui/react-components";
import { Copy, File } from "lucide-react";
import SafeHtml from "../../components/safeHtml/SafeHtml";
import { MilestoneIcon } from "../../views/dashboard/icons/icons";
import HoverCard from "../hoverCard/HoverCard";
import { getCreatePermission, getDeletePermission, getEditPermission, getLabelByType, getTaskPriorityPillClass, getViewPermission } from "../../helpers/utils";
import { KANBAN_ACTIONS } from "./kanban.constants";

const KanbanCard = (props) => {
  const {
    index,
    className = '',
    taskDetails,
    handleAction,
    isOverlay,
  } = props;

  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const [descExpanded, setDescExpanded] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState({});
  const [lane, setLane] = useState([]);

  const allocationHours = taskDetails.allocations ? taskDetails.allocations.reduce((sum, alloc) => sum + alloc.totalHours, 0) : 0;

  const permissions = {
    canView: !!getViewPermission("Tasks"),
    canEdit: !!getEditPermission("Tasks"),
    canDelete: !!getDeletePermission("Tasks"),
    canCreate: !!getCreatePermission("task")
  }
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: taskDetails?.id,
    disabled: !permissions.canEdit || isOverlay || taskDetails?.status === 3, //disable drag on completed tasks 
    data: {
      taskDetails,
      index,
    },
  });



  const handleEdit = () => {
    handleAction({
      actionType: KANBAN_ACTIONS.EDIT,
      payload: {
        id: taskDetails.id,
      }
    });
    setPopoverOpen(null);
  };
  const handleDelete = () => {
    handleAction({
      actionType: KANBAN_ACTIONS.DELETE,
      payload: {
        id: taskDetails.id,
        name: taskDetails.name,
      }
    });
    setPopoverOpen(null);
  };

  const handleMove = (newStatus) => {
    handleAction({
      actionType: KANBAN_ACTIONS.MOVE,
      payload: {
        id: taskDetails.id,
        newStatus: newStatus
      }
    });
    setPopoverOpen(null);
  };

  const handleDuplicate = () => {
    handleAction({
      actionType: KANBAN_ACTIONS.DUPLICATE,
      payload: {
        id: taskDetails.id,
      }
    });
    setPopoverOpen(null);
  };

  const NestedPopOver = () => {
    return (
      <div className="listingPopoverContent">
        {lane.map((item) =>
          TaskStatusMap[item.name] != taskDetails.status && (
            <div className="popItem" onClick={(e) => { e.stopPropagation(); handleMove(item.name) }}>
              {item.icon}
              <p className="text-sm-400 ms-12">{item.name}</p>
            </div>
          )
        )}
      </div>
    );
  };

  useEffect(() => {
    if (taskDetails) {
      const statusList = [
        { id: 4, icon: <OnHoldBadge />, name: "On Hold" },
        { id: 0, icon: <DoneBadge />, name: "In Progress" },
        { id: 1, icon: <InProgressBadge />, name: "Monitoring" },
        { id: 2, icon: <SlippingBadge />, name: "Slipping" },
        { id: 3, icon: <ToDoBadge />, name: "Completed" },
      ];
      const moveToLane = statusList.filter(
        (status) => status.name !== taskDetails.status
      );
      setLane(moveToLane);
    }
  }, [taskDetails]);


  const { inlineItems, overflowItems } = partitionAvatarGroupItems({
    items: taskDetails.allocations || [],
  });

  const handlePopoverOpen = (rowId) => {
    setPopoverOpen(rowId);
  };


  const TaskStatusMap = {
    "In Progress": 0,
    "Monitoring": 1,
    "Slipping": 2,
    "Completed": 3,
    "On Hold": 4,
  }
  // When used as overlay clone, render full card without drag bindings
  if (isOverlay) {
    return (
      <div className={`${styles.cardTaskWrap} ${styles.dragOverlay}`}>
        <div className={`${styles.cardTask}`}>
          <div className="d-flex align-items-start justify-content-between">
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className={styles.cardRefRow}>
                {taskDetails.isMilestone && <MilestoneIcon size={16} />}
                <div className={styles.cardRefId}>{taskDetails.refId}</div>
                <div className="flex gap-1">
                  {taskDetails.progress != null && (
                    <span className={styles.phasePill}>{taskDetails.progress}%</span>
                  )}
                </div>
              </div>
              <div className={styles.cardTitle}>
                {taskDetails.name}
              </div>
              {(taskDetails.startDate || taskDetails.endDate) && (
                <div className={styles.dateSection}>
                  <CalendarIconDate />
                  <span>
                    {taskDetails.startDate
                      ? dateOnlyFormatted(setting, taskDetails.startDate)
                      : ""}
                    {taskDetails.startDate && taskDetails.endDate && " - "}
                    {taskDetails.endDate
                      ? dateOnlyFormatted(setting, taskDetails.endDate)
                      : ""}
                  </span>
                </div>
              )}
              {taskDetails.description && (
                <div className={styles.descriptionWrap}>
                  <SafeHtml
                    className={styles.cardDescription}
                    html={taskDetails.description}
                  />
                </div>
              )}
            </div>
          </div>
          <div className={`${styles.statPills} d-flex`}>
            {taskDetails.attachementsCount > 0 && (
              <div className="mr-1">
                <AttachementIcon count={taskDetails.attachementsCount} />
              </div>
            )}
            {
              taskDetails.isCritical && (
                <span className={`pill AtRisk`}>
                  Critical
                </span>
              )
            }
            <Tooltip content="Priority" relationship="label">
              <span className={getTaskPriorityPillClass(taskDetails.priority)}>
                {getLabelByType("taskPriority", taskDetails.priority)}
              </span>
            </Tooltip>
            {taskDetails.phase && (
              <Tooltip content={`Phase: ${taskDetails.phase}`} relationship="label">
                <span className={styles.phasePill}>{taskDetails.phase}</span>
              </Tooltip>
            )}
          </div>
          {inlineItems.length > 0 && (
            <div className={styles.cardFooter}>
              <div className="AvatarGroupWrapper">
                <AvatarGroup layout="stack" size={24}>
                  {inlineItems.map((item) => (
                    <AvatarGroupItem
                      name={item.resource.name}
                      image={{ src: item.resource.image }}
                      key={item.resource.name}
                    />
                  ))}
                </AvatarGroup>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={styles.cardTaskWrap}
      style={{ opacity: isDragging ? 0.3 : 1 }}
      onClick={handleEdit}
    >
      <div className={`${styles.cardTask} ${className}`} onClick={() => { }}>
        {/* Header: title + menu */}
        <div className="d-flex align-items-start justify-content-between">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className={styles.cardRefRow}>
              {taskDetails.isMilestone && <MilestoneIcon size={18} />}
              <div className={styles.cardRefId}>{taskDetails.refId}</div>
              <div className="flex gap-1 ">
                <span className={`${styles.phasePill} ${styles.totalHours}`}>{allocationHours}h</span>
                {taskDetails.progress != null && (
                  <span className={styles.phasePill}>{taskDetails.progress}%</span>
                )}
                <span className={`${styles.meatballMenu} ${popoverOpen === taskDetails.id ? styles.meatballMenuActive : ''}`}>
                  {(permissions.canView || permissions.canEdit || permissions.canDelete || permissions.canCreate) && (
                    <Popover
                      open={popoverOpen === taskDetails.id}
                      onOpenChange={(e, data) =>
                        handlePopoverOpen(prev => prev === taskDetails.id ? null : taskDetails.id)
                      }
                      positioning="below-end"
                      trapFocus
                    >
                      <PopoverTrigger>
                        <div
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MeatBallsMenuIcon />
                        </div>
                      </PopoverTrigger>
                      <PopoverSurface style={{ zIndex: 1000 }} className="listingPopoverWrapper">
                        <div className="listingPopoverContent">
                          {permissions.canView || permissions.canEdit && (
                            <div className="popItem" onClick={(e) => { e.stopPropagation(); handleEdit(); }}>
                              {permissions.canEdit ? <EditIcon /> : <File size={16} />}
                              <p className="text-sm-400 ms-12">{permissions.canEdit ? 'Edit' : 'View'}</p>
                            </div>
                          )}
                          {permissions.canCreate && (
                            <div
                              className="popItem"
                              onClick={(e) => { e.stopPropagation(); handleDuplicate() }}
                            >
                              <Copy size={16} />
                              <p className="text-sm-400 ms-12">Duplicate</p>
                            </div>
                          )}
                          {permissions.canDelete && (
                            <div
                              className="popItem"
                              onClick={(e) => { e.stopPropagation(); handleDelete() }}
                            >
                              <DeleteIcon />
                              <p className="text-sm-400 ms-12">Delete</p>
                            </div>
                          )}
                          {permissions.canEdit && taskDetails.status !== "Completed" && taskDetails.progress < 100 && (
                            <Popover positioning="after-top" trapFocus>
                              <PopoverTrigger disableButtonEnhancement>
                                <div className="popItem" onClick={(e) => { e.stopPropagation(); }}>
                                  <ChevronRightIcon />
                                  <p className="text-sm-400 ms-12">Move</p>
                                </div>
                              </PopoverTrigger>
                              <PopoverSurface className="listingPopoverWrapper">
                                {NestedPopOver()}
                              </PopoverSurface>
                            </Popover>
                          )}
                        </div>
                      </PopoverSurface>
                    </Popover>
                  )}
                </span>
              </div>
            </div>
            <div className={`${styles.cardTitle}`} >
              {taskDetails.name}
            </div>
            {/* Dates after title */}
            {(taskDetails.startDate || taskDetails.endDate) && (
              <div className={styles.dateSection}>
                <CalendarIconDate />
                <span>
                  {taskDetails.startDate
                    ? dateOnlyFormatted(setting, taskDetails.startDate)
                    : ""}
                  {taskDetails.startDate && taskDetails.endDate && " - "}
                  {taskDetails.endDate
                    ? dateOnlyFormatted(setting, taskDetails.endDate)
                    : ""}
                </span>
              </div>
            )}
            {/* Description with read more */}
            {taskDetails.description && (
              <div className={styles.descriptionWrap}>
                <SafeHtml
                  className={descExpanded ? styles.cardDescriptionExpanded : styles.cardDescription}
                  html={taskDetails.description}
                />
                <span
                  className={styles.readMoreBtn}
                  onClick={(e) => { e.stopPropagation(); setDescExpanded(!descExpanded); }}
                >
                  {taskDetails.description.trim().length <= 100 ? "" : (descExpanded ? "Show less" : "Read more")}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Priority + Phase row */}
        <div className={`${styles.statPills} d-flex`}>
          {taskDetails.attachementsCount > 0 && (
              <div className="mr-1">
                <AttachementIcon count={taskDetails.attachementsCount} />
              </div>
            )}
          {
            taskDetails.isCritical && (
              <span className={`pill AtRisk`}>
                Critical
              </span>
            )
          }
          <Tooltip content="Priority" relationship="label">
            <span className={getTaskPriorityPillClass(taskDetails.priority)}>
              {getLabelByType("taskPriority", taskDetails.priority)}
            </span>
          </Tooltip>
          {taskDetails.phase && (
            <Tooltip content={`Phase: ${taskDetails.phase}`} relationship="label">
              <span className={styles.phasePill}>{taskDetails.phase}</span>
            </Tooltip>
          )}
        </div>

        {/* Footer: assignees only */}
        {inlineItems.length > 0 && (
          <div className={styles.cardFooter}>
            <div className="AvatarGroupWrapper">
              <AvatarGroup layout="stack" size={24}>
                {inlineItems.map((item) => (
                  <HoverCard
                    HoverCardTrigger={
                      <div>
                        <Avatar
                          size={28}
                          color="colorful"
                          name={item.resource.name}
                          image={{ src: item.resource.image || null }}
                        />
                      </div>
                    }
                    AvatarId={item.resource.id}
                    AvatarName={item.resource.name}
                    AvatarImage={item.resource.image}
                    AvatarDesignation={item.resource.title}
                    AvatarEmail={item.resource.email}
                    AvatarPhone={item.resource.phoneNumber}
                    AvatarIsExternal={item.resource.isExternal}
                    CanSendMessage={true}
                  />
                ))}
                {overflowItems && (
                  <AvatarGroupPopover>
                    {overflowItems.map((item) => (
                      <HoverCard
                        HoverCardTrigger={
                          <div>
                            <Avatar
                              size={28}
                              color="colorful"
                              name={item.resource.name}
                              image={{ src: item.resource.image || null }}
                            />
                          </div>
                        }
                        AvatarId={item.resource.id}
                        AvatarName={item.resource.name}
                        AvatarImage={item.resource.image}
                        AvatarDesignation={item.resource.title}
                        AvatarEmail={item.resource.email}
                        AvatarPhone={item.resource.phoneNumber}
                        AvatarIsExternal={item.resource.isExternal}
                        CanSendMessage={true}
                      />
                    ))}
                  </AvatarGroupPopover>
                )}
              </AvatarGroup>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default KanbanCard;
